import { type TillKiosk } from "~/api/tills/types";

import { getDiningOptionsInput } from "./get-dining-options-input";
import { matchesTextInputScope } from "./text-input-scope";

/** Whether the till collects a shopper name on the order-instructions form. */
export function kioskUsesCustomShopperName(till: TillKiosk): boolean {
  return (
    till.config.customShopperName === "required" ||
    till.config.customShopperName === "optional"
  );
}

function hasDiningOptions(till: TillKiosk): boolean {
  return Boolean(getDiningOptionsInput(till.preCheckoutTextInputs));
}

/**
 * Post-checkout text inputs the kiosk renders for a shopper from `groupName`:
 * only free-text inputs, and only those whose scope covers the group.
 */
export function kioskPostCheckoutTextInputs(
  till: TillKiosk,
  groupName: string | null | undefined
): NonNullable<TillKiosk["postCheckoutTextInputs"]> {
  return (till.postCheckoutTextInputs ?? []).filter(
    (input) =>
      input.type === "text" &&
      matchesTextInputScope({
        scope: input.scope,
        schoolId: till.schoolId,
        groupName
      })
  );
}

function hasPostCheckoutInputs(
  till: TillKiosk,
  groupName: string | null | undefined
): boolean {
  return kioskPostCheckoutTextInputs(till, groupName).length > 0;
}

/** Whether the basket must render the order-instructions form for this till. */
export function kioskRequiresOrderInstructions(
  till: TillKiosk,
  groupName: string | null | undefined
): boolean {
  return (
    kioskUsesCustomShopperName(till) ||
    hasDiningOptions(till) ||
    hasPostCheckoutInputs(till, groupName)
  );
}

// Excludes the dining option, which is collected on the landing page (not the
// basket) and so must not hide the menu quick-checkout.
export function kioskMenuRequiresBasket(
  till: TillKiosk,
  groupName: string | null | undefined
): boolean {
  return (
    kioskUsesCustomShopperName(till) || hasPostCheckoutInputs(till, groupName)
  );
}
